import { Button, Css, ModalBody, ModalFooter, ModalHeader, useModal } from "@homebound/beam";
import { observer } from "mobx-react";
import { ChangeEntry } from "../store/InsideMapsConfigStore";

const categoryLabels: Record<ChangeEntry["category"], string> = {
  material: "Material Changes",
  location: "Location Changes",
  "camera-location": "Camera Location Changes",
  verification: "Verification Changes",
};

const categoryOrder: ChangeEntry["category"][] = ["material", "location", "camera-location", "verification"];

interface SaveConfirmationDialogProps {
  changes: ChangeEntry[];
  onConfirm: () => void;
}

export const SaveConfirmationDialog = observer(function SaveConfirmationDialog({
  changes,
  onConfirm,
}: SaveConfirmationDialogProps) {
  const { closeModal } = useModal();

  const grouped = changes.groupBy((c) => c.category);

  return (
    <>
      <ModalHeader>Confirm Save to Blueprint</ModalHeader>
      <ModalBody>
        {changes.length === 0 ? (
          <div css={Css.sm.gray600.$}>No changes detected.</div>
        ) : (
          <div css={Css.df.fdc.gap2.$}>
            <div css={Css.sm.gray700.$}>
              You are about to save <strong>{changes.length}</strong> change{changes.length !== 1 && "s"} to Blueprint:
            </div>
            {categoryOrder
              .filter((cat) => grouped[cat]?.nonEmpty)
              .map((cat) => (
                <div key={cat}>
                  <div css={Css.xsSb.gray500.mb1.add("textTransform", "uppercase").add("letterSpacing", "0.05em").$}>
                    {categoryLabels[cat]} ({grouped[cat].length})
                  </div>
                  <div css={Css.df.fdc.gap1.$}>
                    {grouped[cat].map((change, i) => (
                      <div key={i} css={Css.sm.pyPx(4).pxPx(8).bgGray100.br4.$}>
                        <span css={Css.smSb.$}>{change.cameraName}</span>
                        {change.partName && <span css={Css.gray600.$}> / {change.partName}</span>}
                        {change.textureName && <span css={Css.gray500.$}> / {change.textureName}</span>}
                        <div css={Css.xs.gray700.mt1.$}>{change.description}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button label="Cancel" variant="tertiary" onClick={closeModal} />
        <Button
          label="Confirm Save"
          onClick={() => {
            closeModal();
            onConfirm();
          }}
        />
      </ModalFooter>
    </>
  );
});
